import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleLogin } from '@react-oauth/google';
import { useApp, GIGS } from '@/context/AppContext';
import { SEOHead } from '@/components/SEOHead';
import axios from 'axios';
import {
  LogIn, Mail, ArrowRight, Loader2, AlertCircle,
  CheckCircle2, Clock, ChevronRight, Shield,
  Calendar, FileText, Zap, Sparkles,
} from 'lucide-react';
import { CalendlyModal } from '@/components/ScheduleSection';
import { VisionFunnel } from '@/components/VisionFunnel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Project Selector (multiple orders) ── */
const ProjectSelector = ({ orders, onSelect }) => (
  <div className="space-y-3" data-testid="project-selector">
    <h3 className="text-base font-bold text-slate-900 mb-1">Select a Project</h3>
    <p className="text-xs text-slate-400 mb-4">You have {orders.length} active projects. Choose one to view.</p>
    {orders.map((order) => {
      const gig = GIGS.find(g => g.id === order.gig_id);
      return (
        <button
          key={order.order_id}
          onClick={() => onSelect(order)}
          className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-brand-blue/30 hover:shadow-md transition-all duration-200 group"
          data-testid={`project-${order.order_id}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900 group-hover:text-brand-blue transition-colors">{order.gig_title}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[11px] text-slate-400">{order.order_id}</span>
                <span className="text-slate-200">|</span>
                <span className="text-[11px] text-slate-400">{order.tier_name}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  order.status === 'IN_DEVELOPMENT' ? 'bg-blue-50 text-blue-600' :
                  order.status === 'READY_FOR_REVIEW' ? 'bg-purple-50 text-purple-600' :
                  order.status === 'COMPLETED' ? 'bg-green-50 text-green-600' :
                  'bg-yellow-50 text-yellow-600'
                }`}>{order.status.replace(/_/g, ' ')}</span>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-brand-blue transition-colors" />
          </div>
        </button>
      );
    })}
  </div>
);

/* ═══ MAIN AUTH PAGE ═══ */
export default function AuthPage() {
  const navigate = useNavigate();
  const { user: ctxUser, setUser, setActiveOrder } = useApp();
  const [step, setStep] = useState('login'); // login | otp | projects | no-orders
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orders, setOrders] = useState([]);
  const [authUser, setAuthUser] = useState(null);
  const [showCalendly, setShowCalendly] = useState(false);
  const [showVision, setShowVision] = useState(false);

  // If already authenticated via session, check for orders
  useEffect(() => {
    const token = sessionStorage.getItem('ml_client_token');
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(resp => {
          handleAuthSuccess(resp.data.user, resp.data.orders, token);
        })
        .catch(() => sessionStorage.removeItem('ml_client_token'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthSuccess = (user, userOrders, token) => {
    sessionStorage.setItem('ml_client_token', token);
    setAuthUser(user);
    setUser({ name: user.name || user.email, email: user.email });

    const activeOrders = userOrders.filter(o => ['IN_DEVELOPMENT', 'READY_FOR_REVIEW', 'AWAITING_DOWNPAYMENT'].includes(o.status));

    if (activeOrders.length === 1) {
      loadOrderAndNavigate(activeOrders[0]);
    } else if (activeOrders.length > 1) {
      setOrders(activeOrders);
      setStep('projects');
    } else if (userOrders.length > 0) {
      // Has completed orders only
      setOrders(userOrders);
      setStep('projects');
    } else {
      setStep('no-orders');
    }
  };

  const loadOrderAndNavigate = (order) => {
    const gig = GIGS.find(g => g.id === order.gig_id);
    if (gig) {
      setActiveOrder({
        id: order.order_id, gig,
        tier: gig.tiers[order.tier_index || 0],
        tierIndex: order.tier_index || 0,
        total: order.total, deposit: order.deposit,
        status: order.status, phases: order.phases,
        deliverables: order.deliverables,
      });
    }
    navigate('/dashboard');
  };

  /* ── Google SSO ── */
  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');
    try {
      const resp = await axios.post(`${API}/auth/google`, {
        credential: credentialResponse.credential,
      });
      handleAuthSuccess(resp.data.user, resp.data.orders, resp.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ── Magic Link: Send OTP ── */
  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/magic-link`, { email: email.trim() });
      setStep('otp');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send login code.');
    } finally {
      setLoading(false);
    }
  };

  /* ── Demo Login (instant test access) ── */
  const handleDemoLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await axios.post(`${API}/auth/demo-login`);
      handleAuthSuccess(resp.data.user, resp.data.orders, resp.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Demo login failed.');
    } finally {
      setLoading(false);
    }
  };

  /* ── Magic Link: Verify OTP ── */
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp.trim()) return;
    setLoading(true);
    setError('');
    try {
      const resp = await axios.post(`${API}/auth/verify-otp`, {
        email: email.trim(),
        otp: otp.trim(),
      });
      handleAuthSuccess(resp.data.user, resp.data.orders, resp.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-20" data-testid="auth-page">
      <SEOHead title="Sign In | MediaLabs Client Portal" description="Access your MediaLabs project dashboard." path="/auth" />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-brand-blue/10 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-7 h-7 text-brand-blue" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Client Portal</h1>
          <p className="text-sm text-slate-400 mt-1">Sign in to access your projects</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden">
          <div className="p-6">

            {/* Error */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-4 overflow-hidden"
                >
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-100">
                    <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                    <p className="text-xs text-red-600">{error}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence mode="wait">
              {/* ── LOGIN STEP ── */}
              {step === 'login' && (
                <motion.div key="login" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  {/* Google SSO */}
                  <div className="flex justify-center mb-4" data-testid="google-sso-container">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => setError('Google sign-in was cancelled.')}
                      theme="outline"
                      shape="rectangular"
                      size="large"
                      width="320"
                      text="signin_with"
                    />
                  </div>

                  {/* Divider */}
                  <div className="flex items-center gap-3 my-5">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">or continue with email</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>

                  {/* Email Magic Link */}
                  <form onSubmit={handleSendOTP}>
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Email Address</label>
                    <div className="relative mb-3">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        required
                        className="w-full pl-10 pr-3 py-3 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-shadow"
                        data-testid="email-input"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading || !email.trim()}
                      className="w-full py-3 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                      data-testid="send-otp-btn"
                    >
                      {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Mail className="w-4 h-4" /> Send Login Code</>}
                    </button>
                  </form>

                  <p className="text-[11px] text-slate-400 text-center mt-4 leading-relaxed">
                    Sign in with the email address you used during checkout to access your active projects.
                  </p>

                  {/* Demo Login */}
                  <div className="flex items-center gap-3 mt-5">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">or try a demo</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                  <button
                    onClick={handleDemoLogin}
                    disabled={loading}
                    className="w-full mt-3 py-2.5 rounded-xl border border-brand-blue/20 bg-brand-blue/5 text-brand-blue text-sm font-semibold hover:bg-brand-blue/10 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    data-testid="demo-login-btn"
                  >
                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Logging in...</> : 'Demo: View Client Dashboard'}
                  </button>
                  <p className="text-[10px] text-slate-400 text-center mt-2">
                    Logs in as <strong>testclient@medialabs.ph</strong> with sample projects
                  </p>
                </motion.div>
              )}

              {/* ── OTP STEP ── */}
              {step === 'otp' && (
                <motion.div key="otp" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <div className="text-center mb-5">
                    <div className="w-12 h-12 rounded-full bg-brand-blue/10 flex items-center justify-center mx-auto mb-3">
                      <Mail className="w-5 h-5 text-brand-blue" />
                    </div>
                    <h3 className="text-base font-bold text-slate-900">Check Your Email</h3>
                    <p className="text-xs text-slate-400 mt-1">We sent a 6-digit code to <strong className="text-slate-600">{email}</strong></p>
                  </div>

                  <form onSubmit={handleVerifyOTP}>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      required
                      className="w-full py-4 text-center text-2xl font-bold tracking-[0.5em] rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
                      data-testid="otp-input"
                      autoFocus
                    />
                    <button
                      type="submit"
                      disabled={loading || otp.length !== 6}
                      className="w-full mt-4 py-3 rounded-xl bg-brand-blue text-white text-sm font-bold hover:bg-brand-blue-dark transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                      data-testid="verify-otp-btn"
                    >
                      {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying...</> : <><CheckCircle2 className="w-4 h-4" /> Verify & Sign In</>}
                    </button>
                  </form>

                  <button
                    onClick={() => { setStep('login'); setOtp(''); setError(''); }}
                    className="w-full mt-3 py-2 text-xs text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    &larr; Use a different email
                  </button>
                </motion.div>
              )}

              {/* ── PROJECT SELECTOR ── */}
              {step === 'projects' && (
                <motion.div key="projects" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <ProjectSelector orders={orders} onSelect={loadOrderAndNavigate} />
                </motion.div>
              )}

              {/* ── NO ORDERS → SUPERSIZED ACTIVATION DASHBOARD ── */}
              {step === 'no-orders' && (
                <motion.div key="no-orders" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-center py-2">
                  <Sparkles className="w-8 h-8 text-brand-blue mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-slate-900 mb-1">You're in. Let's build.</h3>
                  <p className="text-xs text-slate-400 mb-4">Choose your path below.</p>
                  <button onClick={() => navigate('/dashboard')} className="w-full py-3 rounded-xl bg-brand-blue text-white text-sm font-bold hover:bg-brand-blue-dark transition-colors inline-flex items-center justify-center gap-2" data-testid="go-to-dashboard-btn">
                    Open Activation Dashboard <ArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Footer link */}
        {step === 'login' && (
          <p className="text-center mt-6 text-xs text-slate-400">
            Don't have a project yet?{' '}
            <button onClick={() => navigate('/services')} className="font-semibold text-brand-orange hover:text-brand-orange-dark transition-colors">
              Browse Services &rarr;
            </button>
          </p>
        )}
      </motion.div>
      <CalendlyModal isOpen={showCalendly} onClose={() => setShowCalendly(false)} />
    </div>
  );
}
